import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Star, TrendingUp, Zap, CheckCircle, ArrowLeft } from 'lucide-react';

const FeatureAdPurchase = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { listingId, listingTitle } = location.state || {};
  
  const [selectedPackage, setSelectedPackage] = useState('featured_7days');

  const packages = [
    {
      id: 'featured_3days',
      name: 'Featured - 3 Days',
      duration: 3,
      price: 1500,
      icon: Star,
      color: 'from-yellow-500 to-orange-500',
      features: [
        'Top position in category',
        'Gold star badge',
        '5x more visibility',
        'Valid for 3 days'
      ]
    },
    {
      id: 'featured_7days',
      name: 'Featured - 7 Days',
      duration: 7,
      price: 3500,
      icon: TrendingUp,
      color: 'from-blue-500 to-purple-500',
      badge: 'POPULAR',
      features: [
        'Top position in category',
        'Gold star badge',
        '5x more visibility',
        'Valid for 7 days',
        'Save Rs. 1,000'
      ]
    },
    {
      id: 'featured_15days',
      name: 'Featured - 15 Days',
      duration: 15,
      price: 7000,
      icon: Zap,
      color: 'from-purple-500 to-pink-500',
      badge: 'BEST VALUE',
      features: [
        'Top position in category',
        'Gold star badge',
        '5x more visibility',
        'Valid for 15 days',
        'Save Rs. 3,500',
        'Priority support'
      ]
    }
  ];

  const selectedPkg = packages.find(p => p.id === selectedPackage);

  const handlePurchase = () => {
    navigate('/payment/select-method', {
      state: {
        serviceType: selectedPackage,
        serviceDuration: selectedPkg.duration,
        amount: selectedPkg.price,
        serviceTitle: selectedPkg.name,
        listingId: listingId,
        listingTitle: listingTitle
      }
    });
  };

  if (!listingId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="feature-ad-no-listing">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-gray-600 mb-4">No listing selected</p>
            <Button onClick={() => navigate('/')} data-testid="feature-ad-go-home">Go to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">⭐ Feature Your Ad</h1>
          <p className="text-lg text-gray-600">Get 5x more visibility with a featured listing</p>
        </div>

        {/* Current Ad Info */}
        <Card className="mb-6 border-blue-200 bg-white shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center text-white text-2xl">
                📢
              </div>
              <div>
                <p className="text-sm text-gray-600">Featuring this ad:</p>
                <p className="text-xl font-bold text-gray-900">{listingTitle || 'Your Listing'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <RadioGroup value={selectedPackage} onValueChange={setSelectedPackage} className="contents">
            {packages.map((pkg, index) => {
              const Icon = pkg.icon;
              return (
                <Card
                  key={pkg.id}
                  className={`cursor-pointer transition-all hover:shadow-xl ${
                    selectedPackage === pkg.id
                      ? 'border-4 border-blue-600 shadow-xl scale-105'
                      : 'border-2 border-gray-200'
                  }`}
                  onClick={() => setSelectedPackage(pkg.id)}
                >
                  <CardHeader className={`bg-gradient-to-r ${pkg.color} text-white rounded-t-lg relative`}>
                    {pkg.badge && (
                      <Badge className="absolute -top-3 right-4 bg-red-600 text-white">
                        {pkg.badge}
                      </Badge>
                    )}
                    <div className="flex items-center gap-3 mb-2">
                      <Icon className="h-8 w-8" />
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value={pkg.id} id={pkg.id} className="border-white" />
                      </div>
                    </div>
                    <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                    <div className="mt-2">
                      <span className="text-4xl font-bold">Rs. {pkg.price.toLocaleString()}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <ul className="space-y-3">
                      {pkg.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </RadioGroup>
        </div>

        {/* Benefits Section */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">🎯 Why Feature Your Ad?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white flex-shrink-0">
                  ⭐
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Top Position</p>
                  <p className="text-sm text-gray-700">Your ad appears at the very top of search results</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white flex-shrink-0">
                  👁️
                </div>
                <div>
                  <p className="font-semibold text-gray-900">5x More Views</p>
                  <p className="text-sm text-gray-700">Featured ads get 5x more clicks and inquiries</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white flex-shrink-0">
                  ⚡
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Sell Faster</p>
                  <p className="text-sm text-gray-700">Featured ads sell 3x faster on average</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Purchase Button */}
        <Card className="shadow-xl">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-sm text-gray-600">Selected Package</p>
                <p className="text-2xl font-bold text-gray-900">{selectedPkg?.name}</p>
                <p className="text-sm text-gray-500">{selectedPkg?.duration} days of featured placement</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="text-4xl font-bold text-blue-600">Rs. {selectedPkg?.price.toLocaleString()}</p>
              </div>
            </div>
            <Button
              onClick={handlePurchase}
              className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-6 text-lg"
            >
              Proceed to Payment
              <Zap className="h-5 w-5 ml-2" />
            </Button>
            <p className="text-center text-sm text-gray-500 mt-3">
              💳 Pay securely via bank transfer • Money-back guarantee
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FeatureAdPurchase;
